import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, ArrowRight, Award, Sparkles, EyeOff, Eye, Save, Mail, MailX, Sun, Moon } from "lucide-react";
import Seo from "@/components/Seo";
import PointsBreakdownCard from "@/components/PointsBreakdownCard";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState(null);

  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [anonymous, setAnonymous] = useState(!!user?.anonymous);
  const [emailSubscribed, setEmailSubscribed] = useState(user?.email_subscribed ?? true);
  const [theme, setTheme] = useState(user?.theme || (typeof window !== "undefined" ? document.body.dataset.theme : "light") || "light");
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    try {
      const [t, p, s] = await Promise.all([
        api.get("/tracks"),
        api.get("/progress"),
        api.get("/me/stats"),
      ]);
      setTracks(t.data);
      setProgress(p.data);
      setStats(s.data);
    } catch (err) {
      console.error("Dashboard: failed to load initial data", err);
    }
  };

  useEffect(() => {
    load();
    // Initial load only — `load` reads stable setters and module-level `api`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDisplayName(user?.display_name || "");
    setAnonymous(!!user?.anonymous);
    setEmailSubscribed(user?.email_subscribed ?? true);
    if (user?.theme) setTheme(user.theme);
  }, [user?.display_name, user?.anonymous, user?.email_subscribed, user?.theme]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/me/settings", {
        display_name: displayName,
        anonymous,
        email_subscribed: emailSubscribed,
        theme,
      });
      document.body.dataset.theme = theme;
      try {
        window.localStorage.setItem("tcpi-theme", theme);
      } catch (storageErr) {
        // Private-mode browsers throw on localStorage writes; theme still applies in-memory.
        console.warn("tcpi-theme persistence unavailable:", storageErr?.message);
      }
      await refresh();
      toast.success(
        anonymous
          ? `Your community name is now "${displayName || "Crown Path Member"}". Your certificate keeps your real name.`
          : "Settings saved."
      );
    } catch (err) {
      toast.error("Could not save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const progressMap = Object.fromEntries(progress.map((p) => [p.track_slug, p]));
  const nextTrack = tracks.find((t) => {
    const p = progressMap[t.slug];
    return !p || !p.monday || !p.wednesday || !p.friday;
  });

  return (
    <div className="bg-tcpi-bg" data-testid="dashboard-page">
      <Seo title="Dashboard" path="/dashboard" noindex />
      <section className="py-16 md:py-20 px-6 md:px-12 max-w-7xl mx-auto">
        {stats?.level === "Crown Fellow" && (
          <Link
            to="/certificate"
            className="block mb-10 rounded-lg border-2 border-[#C9A961] bg-gradient-to-r from-[#C9A961]/15 to-[#8C6A1A]/10 p-6 hover:from-[#C9A961]/20 hover:to-[#8C6A1A]/15 transition"
            data-testid="fellow-celebration-card"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <Crown className="w-12 h-12 text-[#C9A961]" strokeWidth={1.4} />
                <div>
                  <div className="overline mb-1 text-[10px]">Welcome,</div>
                  <div
                    className="text-3xl text-[#C9A961]"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    Crown Fellow {user?.name?.split(" ")[0]}
                  </div>
                  <div className="text-sm text-[#D1CFC9] mt-1">
                    All seven tracks complete. The work continues — see your certificate.
                  </div>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#C9A961]" />
            </div>
          </Link>
        )}

        <DashboardHeader name={user?.name} />

        <StatsTopRow stats={stats} />

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <NextUpCard nextTrack={nextTrack} />
          <PointsBreakdownCard
            breakdown={stats?.breakdown}
            totalTracks={stats?.total_tracks}
            totalPoints={stats?.total_points}
            className="lg:col-span-2"
          />
        </div>

        <PrivacySettingsCard
          displayName={displayName}
          setDisplayName={setDisplayName}
          anonymous={anonymous}
          setAnonymous={setAnonymous}
          emailSubscribed={emailSubscribed}
          setEmailSubscribed={setEmailSubscribed}
          theme={theme}
          setTheme={setTheme}
          savingSettings={savingSettings}
          saveSettings={saveSettings}
          isAdmin={user?.role === "admin"}
        />

        <TracksOverview tracks={tracks} progressMap={progressMap} />
      </section>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function DashboardHeader({ name }) {
  return (
    <>
      <div className="overline mb-3">Your Path</div>
      <h1
        className="text-4xl md:text-5xl text-[#F7F5F0] mb-3"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
      >
        Welcome, {name?.split(" ")[0]}.
      </h1>
      <p className="text-[#D1CFC9]">
        Earning your crown — one track, one session, one honest reflection at a time.
      </p>
    </>
  );
}

function StatsTopRow({ stats }) {
  return (
    <div className="grid md:grid-cols-3 gap-6 mt-10">
      <div className="card-bento" data-testid="stat-points">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Crown Points</div>
          <Sparkles className="w-5 h-5 text-[#C9A961]" strokeWidth={1.4} />
        </div>
        <div className="flex items-end gap-2">
          <span
            className="text-5xl text-[#C9A961]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {stats?.total_points ?? "—"}
          </span>
          <span className="text-sm text-[#8A8782] mb-2">
            / {stats?.total_tracks ? stats.total_tracks * 120 : 840}
          </span>
        </div>
        <ProgressBar pct={stats?.points_pct || 0} />
        <div className="text-sm text-[#8A8782] mt-3">
          {stats?.points_pct || 0}% to mastery
        </div>
      </div>

      <div className="card-bento" data-testid="stat-completion">
        <div className="overline mb-2">Completion</div>
        <div className="flex items-end gap-2">
          <span
            className="text-5xl text-[#F7F5F0]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {stats?.completion_pct ?? 0}
          </span>
          <span className="text-2xl text-[#C9A961] mb-1">%</span>
        </div>
        <ProgressBar pct={stats?.completion_pct || 0} />
        <div className="text-sm text-[#8A8782] mt-3">
          {stats?.tracks_full_complete || 0} of {stats?.total_tracks || 7} tracks fully complete
        </div>
      </div>

      <div className="card-bento" data-testid="stat-level">
        <div className="overline mb-2">Current Level</div>
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-9 h-9 text-[#C9A961]" strokeWidth={1.4} />
          <div
            className="text-2xl text-[#F7F5F0]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {stats?.level || "Crown Candidate"}
          </div>
        </div>
        {stats?.next_level?.name && (
          <div className="text-xs text-[#8A8782] mb-3">
            Reach {stats.next_level.name} at {stats.next_level.tracks_needed} full tracks.
          </div>
        )}
        <Link
          to="/certificate"
          className="text-sm text-[#C9A961] hover:underline inline-flex items-center gap-1"
          data-testid="dashboard-cert-link"
        >
          View certificate <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="mt-4 h-2 bg-[#1A1816] rounded-full overflow-hidden">
      <div
        className="h-full bg-[#C9A961]"
        style={{ width: `${pct}%`, transition: "width 600ms" }}
      />
    </div>
  );
}

function NextUpCard({ nextTrack }) {
  return (
    <div className="card-bento lg:col-span-1" data-testid="stat-next-session">
      <div className="overline mb-2">Next Up</div>
      {nextTrack ? (
        <>
          <div
            className="text-2xl text-[#F7F5F0] leading-tight mb-3"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Track {nextTrack.number}: {nextTrack.title}
          </div>
          <Link
            to={`/tracks/${nextTrack.slug}`}
            className="btn-primary inline-flex items-center gap-2 text-sm"
            data-testid="continue-track-btn"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      ) : (
        <div className="text-[#D1CFC9]">
          <Award className="w-8 h-8 text-[#C9A961] mb-3" />
          All tracks complete. You've earned the Crown Fellow level.
        </div>
      )}
    </div>
  );
}

function PrivacySettingsCard({
  displayName, setDisplayName,
  anonymous, setAnonymous,
  emailSubscribed, setEmailSubscribed,
  theme, setTheme,
  savingSettings, saveSettings,
  isAdmin,
}) {
  return (
    <div className="card-bento mt-6" data-testid="privacy-settings">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="overline mb-1">Community Privacy</div>
          <h3
            className="text-2xl text-[#F7F5F0]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Show up the way you need to.
          </h3>
          <p className="text-sm text-[#8A8782] mt-1">
            Your certificate is always issued in your legal name. In the forum and to fellow
            participants, you can use a private name.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 items-end">
        <div>
          <label className="block text-sm text-[#D1CFC9] mb-2">Community name (alias)</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Path Walker"
            maxLength={40}
            className="input-tcpi"
            data-testid="settings-display-name"
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <ToggleButton
            on={anonymous}
            onClick={() => setAnonymous(!anonymous)}
            iconOn={EyeOff}
            iconOff={Eye}
            labelOn="Anonymous in forum"
            labelOff="Visible by real name"
            testid="settings-anonymous-toggle"
            className="flex-1"
          />
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="settings-save-btn"
          >
            <Save className="w-4 h-4" />
            {savingSettings ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <SettingsRow
        title="Account & security"
        description={`Change your password${isAdmin ? " · manage users · deactivate or remove accounts" : ""}.`}
      >
        <Link to="/account/security" className="btn-secondary text-sm" data-testid="settings-account-link">
          Open
        </Link>
      </SettingsRow>

      <SettingsRow
        title="Appearance"
        description="Light keeps the warm, paper-and-gold look. Dark uses the original deep-night palette. Your choice follows you across devices."
      >
        <div className="flex gap-2" data-testid="settings-theme-toggle">
          <ThemeOption active={theme === "light"} onClick={() => setTheme("light")} icon={Sun} label="Light" testid="theme-light-btn" />
          <ThemeOption active={theme === "dark"} onClick={() => setTheme("dark")} icon={Moon} label="Dark" testid="theme-dark-btn" />
        </div>
      </SettingsRow>

      <SettingsRow
        title="Weekly progress emails"
        description="Receive a gentle weekly digest of your points, your next prompt, and a quote from the path. You can turn this off any time."
      >
        <ToggleButton
          on={emailSubscribed}
          onClick={() => setEmailSubscribed(!emailSubscribed)}
          iconOn={Mail}
          iconOff={MailX}
          labelOn="Subscribed"
          labelOff="Unsubscribed"
          testid="settings-email-toggle"
        />
      </SettingsRow>
    </div>
  );
}

function ToggleButton({ on, onClick, iconOn: IconOn, iconOff: IconOff, labelOn, labelOff, testid, className = "" }) {
  const Icon = on ? IconOn : IconOff;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 rounded-md border transition ${className} ${
        on
          ? "border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]"
          : "border-[#C9A961]/25 text-[#D1CFC9] hover:border-[#C9A961]/55"
      }`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
      <span>{on ? labelOn : labelOff}</span>
    </button>
  );
}

function ThemeOption({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 rounded-md border transition ${
        active
          ? "border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]"
          : "border-[#C9A961]/25 text-[#D1CFC9] hover:border-[#C9A961]/55"
      }`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function SettingsRow({ title, description, children }) {
  return (
    <div className="mt-4 pt-4 border-t border-[#C9A961]/10 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#F7F5F0]">{title}</div>
        <p className="text-xs text-[#8A8782] mt-1">{description}</p>
      </div>
      {children}
    </div>
  );
}

function TracksOverview({ tracks, progressMap }) {
  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-6">
        <h2
          className="text-2xl text-[#F7F5F0]"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          The Seven Tracks
        </h2>
        <Link to="/tracks" className="text-sm text-[#C9A961] hover:underline">
          View all
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {tracks.map((t) => (
          <TrackOverviewCard key={t.slug} track={t} progress={progressMap[t.slug] || {}} />
        ))}
      </div>
    </div>
  );
}

function TrackOverviewCard({ track, progress }) {
  const sessions = ["monday", "wednesday", "friday"];
  const done = sessions.filter((s) => progress[s]).length;
  const allDone = done === 3;
  return (
    <Link
      to={`/tracks/${track.slug}`}
      className="card-bento group block"
      data-testid={`dashboard-track-${track.slug}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="overline mb-2 text-xs">Track {track.number}</div>
          <div
            className="text-xl text-[#F7F5F0] mb-1 group-hover:text-[#C9A961] transition"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {track.title}
          </div>
          <div className="text-sm text-[#8A8782]">{track.course_code}</div>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex gap-1 mb-1">
            {sessions.map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full ${
                  progress[s] ? "bg-[#C9A961]" : "bg-[#C9A961]/20"
                }`}
              />
            ))}
          </div>
          <div className="text-xs text-[#8A8782]">{done}/3</div>
          {allDone && (
            <Crown className="w-4 h-4 text-[#C9A961] mt-2" strokeWidth={1.6} />
          )}
        </div>
      </div>
    </Link>
  );
}
